import { AddIcon, DragHandleIcon } from '@chakra-ui/icons'
import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import {
  DraggableProvided,
  DraggableStateSnapshot,
  Draggable,
  Droppable,
} from '@hello-pangea/dnd'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import { Panel, PanelItem, PanelItemUnit } from '../../../../types/bloodWork'
import ItemRow from './ItemRow'

interface PanelAccordionItemProps {
  panel: Panel
  panelIndex: number
  bloodWorkUnitOptions: React.JSX.Element[]
  dragProvided: DraggableProvided
  dragSnapshot: DraggableStateSnapshot
  updatePanel: (panelId: string, partial: Partial<Panel>) => void
  addPanelItem: (panelId: string) => void
  archivePanel: (panelId: string) => void
  toggleProvocationType: (panelId: string) => void
  updateItem: (
    panelId: string,
    itemId: string,
    partial: Partial<PanelItem>
  ) => void
  updateItemUnit: (
    panelId: string,
    itemId: string,
    unitId: string,
    partial: Partial<PanelItemUnit>
  ) => void
  addItemUnit: (panelId: string, itemId: string) => void
  removeItemUnit: (panelId: string, itemId: string, unitId: string) => void
}

export default function PanelAccordionItem({
  panel,
  panelIndex,
  bloodWorkUnitOptions,
  dragProvided,
  dragSnapshot,
  updatePanel,
  addPanelItem,
  archivePanel,
  toggleProvocationType,
  updateItem,
  updateItemUnit,
  addItemUnit,
  removeItemUnit,
}: PanelAccordionItemProps) {
  // Create wrapped callbacks that include panelId
  const handleUpdateItem = (itemId: string, partial: Partial<PanelItem>) => {
    updateItem(panel.id, itemId, partial)
  }

  const handleUpdateItemUnit = (
    itemId: string,
    unitId: string,
    partial: Partial<PanelItemUnit>
  ) => {
    updateItemUnit(panel.id, itemId, unitId, partial)
  }

  const handleAddItemUnit = (itemId: string) => {
    addItemUnit(panel.id, itemId)
  }

  const handleRemoveItemUnit = (itemId: string, unitId: string) => {
    removeItemUnit(panel.id, itemId, unitId)
  }

  return (
    <AccordionItem
      ref={dragProvided.innerRef}
      {...dragProvided.draggableProps}
      border='1px'
      borderColor={dragSnapshot.isDragging ? 'purple.400' : 'gray.200'}
      borderRadius='md'
      mb={2}
      bg={dragSnapshot.isDragging ? 'purple.50' : 'white'}
    >
      <AccordionButton>
        <Box
          {...dragProvided.dragHandleProps}
          mr={2}
          cursor='grab'
          display='flex'
          alignItems='center'
        >
          <DragHandleIcon />
        </Box>
        <Box flex='1' textAlign='left' fontWeight='medium'>
          {panel.title || 'Untitled Panel'}
        </Box>
        {panel.requiresProvocationType && (
          <Badge colorScheme='purple' mr={2}>
            Provocation Required
          </Badge>
        )}
        {panel.archived && (
          <Badge colorScheme='red' mr={2}>
            Archived
          </Badge>
        )}
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel pb={4}>
        <Stack spacing={3}>
          <FormControl maxW='md'>
            <FormLabel>Panel Title</FormLabel>
            <Input
              placeholder='e.g. Comprehensive Metabolic Panel, Complete Blood Count, etc...'
              value={panel.title}
              onChange={(e) => {
                updatePanel(panel.id, { title: e.target.value })
              }}
            />
          </FormControl>
          <HStack spacing={2}>
            <Button
              size='sm'
              variant='outline'
              leftIcon={<AddIcon />}
              onClick={() => {
                addPanelItem(panel.id)
              }}
            >
              Add Panel Item
            </Button>
            <Button
              size='sm'
              colorScheme={panel.archived ? 'gray' : 'red'}
              variant='outline'
              leftIcon={panel.archived ? <FaEye /> : <FaEyeSlash />}
              onClick={() => {
                archivePanel(panel.id)
              }}
            >
              {panel.archived ? 'Unarchive Panel' : 'Archive Panel'}
            </Button>
            <Checkbox
              size='sm'
              isChecked={panel.requiresProvocationType || false}
              onChange={(e) => {
                toggleProvocationType(panel.id)
              }}
            >
              Requires Provocation Type
            </Checkbox>
          </HStack>
          <TableContainer>
            <Table size='sm' variant='simple'>
              <Thead>
                <Tr>
                  <Th width='40px'></Th>
                  <Th>Name</Th>
                  <Th>Units</Th>
                  <Th>Ref. Rng.</Th>
                  <Th>Opt. Rng.</Th>
                  <Th />
                  <Th />
                </Tr>
              </Thead>
              <Droppable
                droppableId={`panel-items-${panel.id}`}
                type='PANEL_ITEM'
              >
                {(droppableProvided) => (
                  <Tbody
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                  >
                    {panel.items.map((item, itemIndex) => (
                      <Draggable
                        key={item.id}
                        draggableId={`item-${panel.id}-${item.id}`}
                        index={itemIndex}
                      >
                        {(dragProvided, dragSnapshot) => (
                          <ItemRow
                            item={item}
                            updateFunc={(partial) =>
                              handleUpdateItem(item.id, partial)
                            }
                            updateUnitFunc={(unitId, partial) =>
                              handleUpdateItemUnit(item.id, unitId, partial)
                            }
                            addPanelItemUnitFunc={() =>
                              handleAddItemUnit(item.id)
                            }
                            removeUnitFunc={(unitId) =>
                              handleRemoveItemUnit(item.id, unitId)
                            }
                            bloodWorkUnitOptions={bloodWorkUnitOptions}
                            dragProvided={dragProvided}
                            dragSnapshot={dragSnapshot}
                          />
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </Tbody>
                )}
              </Droppable>
            </Table>
          </TableContainer>
        </Stack>
      </AccordionPanel>
    </AccordionItem>
  )
}
